import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { queryClient } from "@/lib/queryClient";
import { registerSuperProperties } from "@/lib/analytics";
import type { Entity, Workspace } from "@shared/schema";

interface EntityContextValue {
  entities: Entity[];
  activeEntity: Entity | null;
  activeEntityId: string | null;
  setActiveEntityId: (id: string | null) => void;
  isAllEntities: boolean;
  displayCurrency: string;
  setDisplayCurrency: (currency: string) => void;
  isLoading: boolean;
}

const EntityContext = createContext<EntityContextValue>({
  entities: [],
  activeEntity: null,
  activeEntityId: null,
  setActiveEntityId: () => {},
  isAllEntities: false,
  displayCurrency: "USD",
  setDisplayCurrency: () => {},
  isLoading: true,
});

const STORAGE_KEY = "octa-active-entity";
const CURRENCY_STORAGE_KEY = "octa-display-currency";

export function EntityProvider({ children }: { children: React.ReactNode }) {
  const [activeEntityId, setActiveEntityIdState] = useState<string | null>(() => {
    try {
      return localStorage.getItem(STORAGE_KEY);
    } catch {
      return null;
    }
  });

  const [displayCurrency, setDisplayCurrencyState] = useState<string>(() => {
    try {
      return localStorage.getItem(CURRENCY_STORAGE_KEY) || "USD";
    } catch {
      return "USD";
    }
  });

  const { data: entities = [], isLoading: entitiesLoading } = useQuery<Entity[]>({
    queryKey: ["/api/entities"],
  });

  const { data: workspace, isLoading: workspaceLoading } = useQuery<Workspace>({
    queryKey: ["/api/workspace"],
  });

  const isLoading = entitiesLoading || workspaceLoading;
  const isAllEntities = activeEntityId === "all";

  const _epLoopRef = useRef<{ count: number; prev: Record<string, string>; log: string[]; sent: boolean; timer: any }>({ count: 0, prev: {}, log: [], sent: false, timer: 0 });
  (() => {
    const lr = _epLoopRef.current;
    lr.count++;
    clearTimeout(lr.timer);
    lr.timer = setTimeout(() => { lr.count = 0; lr.prev = {}; lr.log = []; lr.sent = false; }, 0);
    const snap: Record<string, string> = {
      activeEntityId: activeEntityId ?? "null", displayCurrency,
      entitiesLoading: String(entitiesLoading), workspaceLoading: String(workspaceLoading),
      entityCount: String(entities.length), isAllEntities: String(isAllEntities),
    };
    const ch: string[] = [];
    for (const k of Object.keys(snap)) if (lr.prev[k] !== undefined && lr.prev[k] !== snap[k]) ch.push(`${k}:${lr.prev[k]}->${snap[k]}`);
    lr.prev = snap;
    if (lr.count > 2) lr.log.push(`R${lr.count}:${ch.length ? ch.join(",") : "SAME"}`);
    if (lr.count >= 8 && !lr.sent) {
      lr.sent = true;
      const msg = `ENTITY_RENDER_LOOP(${lr.count})|trail=[${lr.log.join(";")}]|snap=${JSON.stringify(snap)}`;
      try { (window as any).__EP_LOOP_DIAG = msg; } catch {}
      try {
        const xhr = new XMLHttpRequest();
        xhr.open("POST", "/api/client-error", false);
        xhr.setRequestHeader("Content-Type", "application/json");
        xhr.send(JSON.stringify({ message: msg, url: window.location.href, category: "render_loop" }));
      } catch {}
    }
  })();

  const broadcastChannelRef = useRef<BroadcastChannel | null>(null);

  useEffect(() => {
    try {
      const channel = new BroadcastChannel("octa-entity-sync");
      broadcastChannelRef.current = channel;
      channel.onmessage = (event) => {
        if (event.data?.type === "entity-change" && event.data.entityId !== undefined) {
          setActiveEntityIdState(event.data.entityId);
          queryClient.invalidateQueries({
            predicate: (query) => {
              const key = query.queryKey[0];
              if (typeof key !== "string") return true;
              if (key === "/api/auth/me" || key === "/api/workspace/features" || key === "/api/workspace/profile-config" || key === "/api/complexity-dashboard-counts") return false;
              return true;
            },
          });
        }
        if (event.data?.type === "auth-change") {
          queryClient.invalidateQueries();
          window.location.reload();
        }
      };
      return () => channel.close();
    } catch {
      return undefined;
    }
  }, []);

  useEffect(() => {
    const handleStorageEvent = (event: StorageEvent) => {
      if (event.key === STORAGE_KEY && event.newValue !== activeEntityId) {
        setActiveEntityIdState(event.newValue);
        queryClient.invalidateQueries({
          predicate: (query) => {
            const key = query.queryKey[0];
            if (typeof key !== "string") return true;
            if (key === "/api/auth/me" || key === "/api/workspace/features" || key === "/api/workspace/profile-config" || key === "/api/complexity-dashboard-counts") return false;
            return true;
          },
        });
      }
    };
    window.addEventListener("storage", handleStorageEvent);
    return () => window.removeEventListener("storage", handleStorageEvent);
  }, [activeEntityId]);

  const setActiveEntityId = useCallback((id: string | null) => {
    setActiveEntityIdState(id);
    if (id) {
      localStorage.setItem(STORAGE_KEY, id);
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
    queryClient.invalidateQueries({
      predicate: (query) => {
        const key = query.queryKey[0];
        if (typeof key !== "string") return true;
        if (key === "/api/auth/me") return false;
        if (key === "/api/workspace/features") return false;
        if (key === "/api/workspace/profile-config") return false;
        if (key === "/api/complexity-dashboard-counts") return false;
        return true;
      },
    });
    try {
      broadcastChannelRef.current?.postMessage({ type: "entity-change", entityId: id });
    } catch {}
  }, []);

  const setDisplayCurrency = useCallback((currency: string) => {
    setDisplayCurrencyState(currency);
    try {
      localStorage.setItem(CURRENCY_STORAGE_KEY, currency);
    } catch {}
  }, []);

  const entitySigRef = useRef("");
  useEffect(() => {
    if (isLoading || entities.length === 0) return;
    const sig = entities.map(e => e.id).sort().join(",") + "|" + (entities.find(e => e.isDefault)?.id ?? "");
    const needsValidation = !activeEntityId || (activeEntityId !== "all" && !entities.some(e => e.id === activeEntityId));
    if (entitySigRef.current === sig && !needsValidation) {
      return;
    }
    entitySigRef.current = sig;
    if (!activeEntityId) {
      const defaultEntity = entities.find(e => e.isDefault) || entities[0];
      setActiveEntityId(defaultEntity.id);
      return;
    }
    if (activeEntityId !== "all") {
      const exists = entities.some(e => e.id === activeEntityId);
      if (!exists) {
        const defaultEntity = entities.find(e => e.isDefault) || entities[0];
        setActiveEntityId(defaultEntity.id);
      }
    }
  }, [entities, isLoading, activeEntityId, setActiveEntityId]);

  const prevEntityRef = useRef<string | null>(null);
  useEffect(() => {
    if (isLoading) return;
    if (prevEntityRef.current === activeEntityId) return;
    prevEntityRef.current = activeEntityId;
    let resolvedCurrency = displayCurrency;
    if (isAllEntities) {
      resolvedCurrency = workspace?.defaultCurrency || "USD";
      setDisplayCurrency(resolvedCurrency);
    } else {
      const entity = entities.find(e => e.id === activeEntityId);
      if (entity?.currency) {
        resolvedCurrency = entity.currency;
        setDisplayCurrency(resolvedCurrency);
      }
    }
    registerSuperProperties({ active_entity_id: activeEntityId, display_currency: resolvedCurrency });
  }, [activeEntityId, isAllEntities, entities, workspace, isLoading]);

  const activeEntity = isAllEntities ? null : (entities.find(e => e.id === activeEntityId) || null);

  return (
    <EntityContext.Provider value={{
      entities, activeEntity, activeEntityId, setActiveEntityId,
      isAllEntities, displayCurrency, setDisplayCurrency, isLoading,
    }}>
      {children}
    </EntityContext.Provider>
  );
}

export function useActiveEntity() {
  return useContext(EntityContext);
}
